"use client";

import { useState, useEffect, useRef, useCallback, MouseEventHandler, useMemo } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import React from "react";
import styled, { useTheme } from "styled-components";
import { BoxProps } from "../Box";
import Flex from "../Flex";
import { markdownStyle } from "../../constants/markdownStyle";
import { AnyObject } from "../../models";
import factory from "../../testUtils/factory";
import LinkPopupMenu from "./components/LinkPopupMenu";
import Toolbar from "./components/Toolbar";

import useTurndownService from "./hooks/useTurndownService";
import useMarkdownIt from "./hooks/useMarkdownIt";
import SelectAllExtension from "./utils/SelectAllExtension";
import HighlightExtension from "./utils/HighlightExtension";
import TextColorExtension from "./utils/TextColorExtension";
import { useEditorColors } from "./hooks/useEditorColors";
import useBoolean from "../../hooks/useBoolean";
import { updateLinksOnRender } from "./utils/helpers";
import { SpacingValue } from "../ComponentProps";
import PasteHandler from "./utils/PasteHandler";

const StyledEditor = styled(EditorContent)<{
  blockquotecolor: string;
  backgroundcolor: string;
  p?: SpacingValue;
}>`
  background-color: ${props => props.backgroundcolor};
  word-break: break-word;
  border-radius: 4px;
  padding: ${props => {
    if (props.p) return props.p;
    return props.backgroundcolor?.toLowerCase() === "#ffffff" ? "4px" : "16px";
  }};
  flex: 1 1 100%;
  ${markdownStyle};
  blockquote {
    color: ${props => props.blockquotecolor};
  }
  code {
    color: black;
  }
  & .tiptap {
    outline: none;
    :focus-visible {
      outline: none;
    }
  }
`;

const extensions = [
  StarterKit,
  Underline,
  Link.configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: "https",
    protocols: ["http", "https"],
    shouldAutoLink: url => url.startsWith("https://"),
    isAllowedUri: (url, ctx) => {
      return ctx.defaultValidate(url);
    },
  }),
  SelectAllExtension,
  HighlightExtension,
  TextColorExtension,
  PasteHandler,
];

type Props = {
  itemId: string | number;
  content: string | undefined;
  buildSaveData: (markdown: string) => AnyObject;
  selected?: boolean;
  canEdit?: boolean;
  isEditing?: boolean;
  hasDefaultValue?: boolean;
  onBeginEditing?: (id: string | number) => void;
  onFinishEditing?: (data: AnyObject | null) => void;
  onClick?: MouseEventHandler;
  backgroundColorMenu?: React.ReactNode;
  backgroundColor?: string;
  onAIGenerate?: () => void;
  onContentChange?: (markdown: string) => void;
} & BoxProps;

export const SaveDataBuilders = {
  noteContent: (markdown: string) => ({ data: { content: markdown } }),
  caption: (markdown: string) => ({ display_properties: { caption: markdown } }),
};

const TextEditor: React.FC<Props> = ({
  itemId,
  content: rawContent,
  buildSaveData,
  selected,
  canEdit,
  isEditing,
  hasDefaultValue,
  onBeginEditing,
  onFinishEditing,
  onClick,
  backgroundColorMenu,
  backgroundColor: backgroundColorProp,
  onAIGenerate,
  onContentChange,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [editingLabel, setEditingLabel] = useState<boolean>(false);
  const [colorOptionsShown, showColorOptions, hideColorOptions] = useBoolean(false);
  const [inlineColorMenuOpen, setInlineColorMenuOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const textOptionRef = useRef<HTMLDivElement>(null);
  const colorOptionRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const overflowOptionRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const id = useMemo(() => factory.genID(), []);
  const source = id.toString().concat("-label-editor");
  const { turndownService } = useTurndownService();
  const { content } = useMarkdownIt({ content: rawContent });
  const contentWithAutolink = updateLinksOnRender(content);

  const { backgroundColor, color, blockquoteColor } = useEditorColors(backgroundColorProp, theme);

  const editor: Editor | null = useEditor({
    extensions,
    content: contentWithAutolink,
    autofocus: false,
    editable: false,
    immediatelyRender: false,
    editorProps: {
      handleKeyDown: (_, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          event.preventDefault();
          handleFinishEditing();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    if (isEditing) {
      setIsFocused(true);
      editor.setOptions({ editable: true });

      if (hasDefaultValue) {
        editor.commands.focus("all");
      } else {
        editor.commands.focus("end");
      }
    } else {
      setIsFocused(false);
      editor.setOptions({ editable: false });
    }
  }, [editor, isEditing, hasDefaultValue]);

  useEffect(() => {
    if (!editor || !onContentChange) return;
    const handler = () => {
      const html = editor.getHTML();
      const modifiedHTML = html.replace(/<p><\/p>/g, "<p><br></p>");
      const isEmpty =
        modifiedHTML.replace(/<p><br\s*\/?><\/p>/g, "").replace(/\s+/g, "").length === 0;
      const markdown = isEmpty ? "" : turndownService.turndown(modifiedHTML);
      onContentChange(markdown);
    };
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
    };
  }, [editor, onContentChange, turndownService]);

  useEffect(() => {
    if (!editor || isFocused) return;
    editor.commands.setContent(contentWithAutolink);
  }, [editor, contentWithAutolink, isFocused]);

  useEffect(() => {
    if (!isFocused && editingLabel === true) {
      setEditingLabel(false);
    }
  }, [isFocused, editingLabel]);

  useEffect(() => {
    if (!selected) {
      setIsFocused(false);
      editor?.setOptions({ editable: false });
    }
  }, [selected, editor]);

  useEffect(() => {
    if (!editor || isEditing) return;
    editor.commands.setContent(contentWithAutolink);
  }, [editor, contentWithAutolink, isEditing]);

  const handleFinishEditing = useCallback(() => {
    setIsFocused(false);
    const html = editor?.getHTML();
    const modifiedHTML = html.replace(/<p><\/p>/g, "<p><br></p>");
    const isEmpty =
      modifiedHTML.replace(/<p><br\s*\/?><\/p>/g, "").replace(/\s+/g, "").length === 0;
    const markdown = isEmpty ? "" : turndownService.turndown(modifiedHTML);

    if (markdown.trim() === (rawContent || "").trim()) {
      onFinishEditing?.(null);
      return;
    }

    onFinishEditing?.(buildSaveData(markdown));
  }, [editor, rawContent, buildSaveData, onFinishEditing, turndownService]);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!isFocused) return;
      if (
        toolbarRef.current?.contains(event.target as Node) ||
        rootRef.current?.contains(event.target as Node) ||
        labelRef.current?.contains(event.target as Node) ||
        textOptionRef.current?.contains(event.target as Node) ||
        colorOptionRef.current?.contains(event.target as Node) ||
        overflowOptionRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      handleFinishEditing();
    },
    [handleFinishEditing, isFocused]
  );

  const beginEditing = useCallback(() => {
    onBeginEditing?.(itemId);
  }, [itemId, onBeginEditing]);

  useEffect(() => {
    if (!selected) return;
    if (colorOptionsShown || inlineColorMenuOpen) return;
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [
    handleFinishEditing,
    selected,
    isFocused,
    handleClickOutside,
    colorOptionsShown,
    inlineColorMenuOpen,
  ]);

  if (!editor) return null;

  const editorProps = {
    id: "editor",
    editor,
    onDoubleClick: canEdit ? beginEditing : null,
    onClick,
    color,
  };

  return (
    <Flex flexDirection="column" width="100%" ref={rootRef}>
      <div ref={sentinelRef} style={{ position: "absolute", top: 0 }} />
      {isFocused && (
        <Toolbar
          editor={editor}
          setEditingLabel={setEditingLabel}
          source={source}
          backgroundColorMenu={backgroundColorMenu}
          onInlineColorMenuChange={setInlineColorMenuOpen}
          refs={{
            textOptionRef,
            rootRef,
            sentinelRef,
            toolbarRef,
            colorOptionRef,
            overflowOptionRef,
          }}
          colorProps={{
            buttonBgColor: backgroundColor,
            colorOptionsShown,
            showColorOptions,
            hideColorOptions,
          }}
          onAIGenerate={onAIGenerate}
        />
      )}
      {editingLabel && (
        <LinkPopupMenu
          editor={editor}
          setEditingLabel={setEditingLabel}
          source={source}
          labelRef={labelRef}
          isFocused={isFocused}
        />
      )}
      <StyledEditor
        data-testid="tiptap-editor"
        blockquotecolor={blockquoteColor}
        backgroundcolor={backgroundColor}
        {...editorProps}
        {...rest}
      />
    </Flex>
  );
};

export default TextEditor;
